import { Link, useRouter } from "@tanstack/react-router";
import { CheckCircle, Eye, EyeOff, Lock } from "lucide-react";
import {
  type ChangeEvent,
  type SyntheticEvent,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { supabase } from "../lib/supabase";
import { clearFieldError } from "../lib/validation";
import ThemeToggle from "./ThemeToggle";

type FormErrors = {
  password?: string;
  confirmPassword?: string;
  global?: string;
};

export default function ResetPassword() {
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [success, setSuccess] = useState(false);
  const [validToken, setValidToken] = useState<boolean | null>(null);
  const errorId = useId();
  const passwordRef = useRef<HTMLInputElement | null>(null);
  const confirmPasswordRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    // Check if we have a valid recovery session.
    // On native, AuthGuard intercepts the deep-link URL, calls
    // supabase.auth.setSession() with the recovery tokens, and navigates here,
    // so by the time this component mounts the session is already established.
    // On web, the Supabase client auto-detects the hash fragment on init.
    const checkRecoverySession = async () => {
      try {
        const {
          data: { session },
          error,
        } = await supabase.auth.getSession();

        if (error) {
          console.error("🔴 Error checking session:", error);
          setValidToken(false);
          setFormErrors({
            global:
              "Invalid or expired password reset link. Please request a new one.",
          });
          return;
        }

        if (session) {
          setValidToken(true);
        } else {
          setValidToken(false);
          setFormErrors({
            global:
              "Invalid or expired password reset link. Please request a new one.",
          });
        }
      } catch (err) {
        console.error("🔴 Error in recovery check:", err);
        setValidToken(false);
        setFormErrors({
          global: "An error occurred. Please try again.",
        });
      }
    };

    checkRecoverySession();
  }, []);

  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!password.trim()) {
      errors.password = "Please enter a new password.";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters.";
    }

    if (!confirmPassword.trim()) {
      errors.confirmPassword = "Please confirm your password.";
    } else if (password !== confirmPassword) {
      errors.confirmPassword = "Passwords do not match.";
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);

      // Focus first invalid field
      if (errors.password) {
        passwordRef.current?.focus();
      } else if (errors.confirmPassword) {
        confirmPasswordRef.current?.focus();
      }

      return false;
    }

    return true;
  };

  const handleSubmit = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormErrors({});

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        throw error;
      }

      // Success!
      setSuccess(true);

      // Redirect to home after a brief delay
      setTimeout(() => {
        void router.navigate({ to: "/inventory" });
      }, 2000);
    } catch (err) {
      console.error("🔴 Password reset error:", err);
      setFormErrors({
        global:
          err instanceof Error
            ? err.message
            : "Failed to reset password. Please try again.",
      });
      setLoading(false);
    }
  };

  if (validToken === null) {
    // Loading state while checking token
    return (
      <div className="pt-safe flex min-h-screen flex-col overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
        {/* Top gradient orb blobs */}
        <div className="relative inset-0 w-full">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
          />
        </div>

        {/* Bottom gradient orb blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
        />

        {/* Header */}
        <header className="relative w-full">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <img
                src="/favicon-browser.svg"
                alt="Pantry logo"
                className="h-8 w-8"
              />
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                Pantry
              </span>
            </div>
            <ThemeToggle />
          </div>
        </header>

        {/* Main content */}
        <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
            <div className="flex items-center justify-center">
              <div
                className="h-8 w-8 animate-spin rounded-full border-4 border-blue-200 border-t-blue-600 dark:border-blue-700 dark:border-t-blue-400"
                aria-hidden="true"
              ></div>
            </div>
            <p className="mt-4 text-center text-sm text-gray-600 dark:text-gray-400">
              Verifying reset link...
            </p>
          </div>
        </main>

        {/* Footer */}
        <footer className="pb-safe relative mb-6 px-4 py-6 text-center">
          <Link
            to="/privacy"
            className="text-sm text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
          >
            Privacy Policy
          </Link>
        </footer>
      </div>
    );
  }

  if (validToken === false) {
    // Invalid or expired token
    return (
      <div className="pt-safe flex min-h-screen flex-col overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
        {/* Top gradient orb blobs */}
        <div className="relative inset-0 w-full">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
          />
        </div>

        {/* Bottom gradient orb blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
        />

        {/* Header */}
        <header className="relative w-full">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <img
                src="/favicon-browser.svg"
                alt="Pantry logo"
                className="h-8 w-8"
              />
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                Pantry
              </span>
            </div>
            <ThemeToggle />
          </div>
        </header>

        {/* Main content */}
        <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-700 dark:bg-red-900/30">
              <p className="font-medium text-red-800 dark:text-red-300">
                Invalid Reset Link
              </p>
              <p className="mt-2 text-sm text-red-700 dark:text-red-400">
                {formErrors.global ||
                  "This password reset link is invalid or has expired. Please request a new one."}
              </p>
            </div>

            <button
              type="button"
              onClick={() => void router.navigate({ to: "/inventory" })}
              className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              Return to Login
            </button>
          </div>
        </main>

        {/* Footer */}
        <footer className="pb-safe relative mb-6 px-4 py-6 text-center">
          <Link
            to="/privacy"
            className="text-sm text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
          >
            Privacy Policy
          </Link>
        </footer>
      </div>
    );
  }

  if (success) {
    // Success state
    return (
      <div className="pt-safe flex min-h-screen flex-col overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
        {/* Top gradient orb blobs */}
        <div className="relative inset-0 w-full">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
          />
        </div>

        {/* Bottom gradient orb blobs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
        />

        {/* Header */}
        <header className="relative w-full">
          <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center gap-3">
              <img
                src="/favicon-browser.svg"
                alt="Pantry logo"
                className="h-8 w-8"
              />
              <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
                Pantry
              </span>
            </div>
            <ThemeToggle />
          </div>
        </header>

        {/* Main content */}
        <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-8">
          <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
            <div className="mb-6 flex justify-center">
              <CheckCircle className="h-16 w-16 text-green-600 dark:text-green-400" />
            </div>

            <h2 className="mb-2 text-center text-xl font-semibold text-gray-800 dark:text-gray-100">
              Password Reset Successful!
            </h2>
            <p className="mb-6 text-center text-sm text-gray-600 dark:text-gray-400">
              Your password has been updated. You can now sign in with your new
              password.
            </p>

            <p className="text-center text-sm text-gray-500 dark:text-gray-500">
              Redirecting to login...
            </p>
          </div>
        </main>

        {/* Footer */}
        <footer className="pb-safe relative mb-6 px-4 py-6 text-center">
          <Link
            to="/privacy"
            className="text-sm text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
          >
            Privacy Policy
          </Link>
        </footer>
      </div>
    );
  }

  return (
    <div className="pt-safe flex min-h-screen flex-col overflow-x-clip bg-white text-gray-900 dark:bg-gray-950 dark:text-white">
      {/* Top gradient orb blobs */}
      <div className="relative inset-0 w-full">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-40 left-1/4 h-150 w-150 -translate-x-1/2 rounded-full bg-blue-400/20 blur-3xl dark:bg-blue-600/15"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-20 right-0 h-125 w-125 translate-x-1/3 rounded-full bg-indigo-400/20 blur-3xl dark:bg-indigo-600/15"
        />
      </div>

      {/* Bottom gradient orb blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 h-100 w-100 -translate-x-1/4 rounded-full bg-blue-400/15 blur-3xl dark:bg-blue-600/10"
      />

      {/* Header */}
      <header className="relative w-full">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <img
              src="/favicon-browser.svg"
              alt="Pantry logo"
              className="h-8 w-8"
            />
            <span className="text-2xl font-bold text-gray-800 dark:text-gray-100">
              Pantry
            </span>
          </div>
          <ThemeToggle />
        </div>
      </header>

      {/* Main content */}
      <main className="relative flex flex-1 flex-col items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800">
          <div className="mb-6 flex items-center gap-2">
            <Lock className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
              Reset Your Password
            </h2>
          </div>

          <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">
            Enter your new password below. Make sure it's at least 6 characters
            long and something you'll remember.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div>
              <label
                htmlFor="password"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                New Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  ref={passwordRef}
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setPassword(e.target.value);
                    if (formErrors.password || formErrors.global) {
                      setFormErrors((prev) => ({
                        ...clearFieldError(prev, "password"),
                        global: undefined,
                      }));
                    }
                  }}
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  aria-describedby={
                    formErrors.password
                      ? "password-error"
                      : formErrors.global
                        ? errorId
                        : undefined
                  }
                  aria-invalid={!!formErrors.password || !!formErrors.global}
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.password && (
                <p
                  id="password-error"
                  className="mt-1 text-sm text-red-700 dark:text-red-400"
                >
                  {formErrors.password}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  ref={confirmPasswordRef}
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    setConfirmPassword(e.target.value);
                    if (formErrors.confirmPassword || formErrors.global) {
                      setFormErrors((prev) => ({
                        ...clearFieldError(prev, "confirmPassword"),
                        global: undefined,
                      }));
                    }
                  }}
                  autoComplete="new-password"
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 pr-10 text-gray-800 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                  aria-describedby={
                    formErrors.confirmPassword
                      ? "confirm-password-error"
                      : formErrors.global
                        ? errorId
                        : undefined
                  }
                  aria-invalid={
                    !!formErrors.confirmPassword || !!formErrors.global
                  }
                  required
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded-sm p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  tabIndex={-1}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {formErrors.confirmPassword && (
                <p
                  id="confirm-password-error"
                  className="mt-1 text-sm text-red-700 dark:text-red-400"
                >
                  {formErrors.confirmPassword}
                </p>
              )}
            </div>

            {formErrors.global && (
              <div
                id={errorId}
                role="alert"
                className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-700 dark:bg-red-900/30 dark:text-red-400"
              >
                {formErrors.global}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !password.trim() || !confirmPassword.trim()}
              className="w-full rounded-lg bg-blue-600 py-2 font-medium text-white transition-opacity hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:bg-blue-500 dark:hover:bg-blue-600"
            >
              {loading ? "Updating Password..." : "Reset Password"}
            </button>
          </form>

          <div className="mt-6 rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-700 dark:bg-blue-900/30">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              <strong>💡 Tip:</strong> Choose a strong password that you haven't
              used elsewhere. Consider using a password manager to keep track of
              your passwords securely.
            </p>
          </div>

          <button
            type="button"
            onClick={() => void router.navigate({ to: "/inventory" })}
            disabled={loading}
            className="mt-4 w-full rounded-sm text-sm font-medium text-blue-600 transition-opacity hover:text-blue-700 disabled:pointer-events-none disabled:opacity-50 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Back to Login
          </button>
        </div>
      </main>

      {/* Footer */}
      <footer className="pb-safe relative mb-6 px-4 py-6 text-center">
        <Link
          to="/privacy"
          className="text-sm text-gray-400 hover:text-gray-600 dark:text-gray-600 dark:hover:text-gray-400"
        >
          Privacy Policy
        </Link>
      </footer>
    </div>
  );
}
